import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/api';
import { AccountPosition } from '../types';
import { TierBudgetSnapshot } from '../types/tier1Portfolio';
import LoadingSpinner from './LoadingSpinner';
import { Card, CardContent, CardHeader, CardTitle } from './Card';
import TierBadge from './portfolio/TierBadge';
import TierBudgetCard from './portfolio/TierBudgetCard';
import { fmtUsd } from './portfolio/format';
import TierPerformanceOverTimeChart from './portfolio/TierPerformanceOverTimeChart';
import {
  countGenerationSlots,
  filterTier6ThesisPositions,
} from '../utils/tierPositionSlots';

interface Props {
  accountId: string;
  tierId: string;
  tierName: string;
  maxPositions?: number;
}

const formatDateTime = (value?: string) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const positionMarketValue = (p: AccountPosition): number => {
  const mv = p.actualPosition?.schwabInstrumentMarketValue;
  if (mv != null && !Number.isNaN(mv)) return Math.abs(mv);
  return p.allocatedCapital ?? 0;
};

const Tier6OptionsPanel: React.FC<Props> = ({
  accountId,
  tierId,
  tierName,
  maxPositions,
}) => {
  const [loading, setLoading] = useState(true);
  const [budget, setBudget] = useState<TierBudgetSnapshot | null>(null);
  const [positions, setPositions] = useState<AccountPosition[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [budgets, posRes] = await Promise.all([
        apiService.getTierBudgets(accountId),
        apiService.getAccountPositions(accountId),
      ]);
      setBudget(budgets.find((b) => b.tierId === tierId) ?? null);
      setPositions(posRes.positions ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load Tier 6 data');
    } finally {
      setLoading(false);
    }
  }, [accountId, tierId]);

  useEffect(() => {
    void load();
  }, [load]);

  const thesisPositions = useMemo(
    () => filterTier6ThesisPositions(positions, tierId),
    [positions, tierId],
  );

  const openSlots = useMemo(
    () => countGenerationSlots(positions, tierId),
    [positions, tierId],
  );

  const maxSlots = maxPositions ?? 3;

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900">Tier 6 — {tierName}</h2>
            <TierBadge kind="tier6" />
          </div>
          <p className="text-sm text-gray-500">
            Thesis-driven options orchestration · budget and open slots for this tier only.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      <TierPerformanceOverTimeChart accountId={accountId} riskTier={6} />

      {budget ? (
        <>
          <TierBudgetCard
            label="Tier 6"
            tierBudgetUsd={budget.tierBudgetUsd}
            tierDeployedUsd={budget.tierDeployedUsd}
            tierAvailableUsd={budget.tierAvailableUsd}
            allocationPct={budget.allocationPct}
            extra={
              <div>
                <p className="text-xl font-semibold">
                  {openSlots} / {maxSlots}
                </p>
                <p className="text-xs text-gray-500">Generation slots used</p>
              </div>
            }
          />
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full bg-violet-500 transition-all"
              style={{
                width: `${Math.min(100, maxSlots > 0 ? (openSlots / maxSlots) * 100 : 0)}%`,
              }}
            />
          </div>
        </>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Thesis positions</CardTitle>
        </CardHeader>
        <CardContent>
          {thesisPositions.length === 0 ? (
            <div className="text-sm text-gray-600">
              <p>No Tier 6 orchestrator positions yet.</p>
              <p className="mt-2">
                <Link to="/orchestration-audits" className="text-primary-600 hover:underline">
                  View orchestration audits
                </Link>{' '}
                after thesis runs.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">
                      Symbol
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">
                      Status
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">
                      Thesis TF
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">
                      Alloc / MV
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">
                      Updated
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {thesisPositions.map((p) => (
                    <tr key={p._id}>
                      <td className="whitespace-nowrap px-3 py-2 font-medium">{p.symbol}</td>
                      <td className="whitespace-nowrap px-3 py-2">{p.status.replace(/_/g, ' ')}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                        {p.associatedThesisTimeframe ?? '—'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        {fmtUsd(positionMarketValue(p))}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-gray-500">
                        {formatDateTime(p.updatedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Tier6OptionsPanel;
